import { useEffect, useState } from "react";
import { Heart, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { fetchIntentions, createIntention, prayForIntention } from "@/lib/api";
import { TIDS } from "@/constants/testIds";

const formatDate = (d) => {
    try {
        return new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
    } catch (e) {
        return "";
    }
};

const PrayerWall = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [name, setName] = useState("");
    const [intention, setIntention] = useState("");
    const [hp, setHp] = useState(""); // honeypot
    const [submitting, setSubmitting] = useState(false);
    const [prayed, setPrayed] = useState(() => {
        try {
            return new Set(JSON.parse(localStorage.getItem("htk-prayed") || "[]"));
        } catch (e) {
            return new Set();
        }
    });

    const load = async () => {
        try {
            const data = await fetchIntentions();
            setItems(data);
        } catch (e) {
            toast.error("Could not load prayer intentions.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim() || intention.trim().length < 3) {
            toast.error("Please share your name and an intention (at least 3 characters).");
            return;
        }
        setSubmitting(true);
        try {
            await createIntention({ name: name.trim(), intention: intention.trim(), hp });
            toast.success("Your intention has been added, we'll pray for you.");
            setName("");
            setIntention("");
            setHp("");
            await load();
        } catch (e) {
            if (e?.response?.status === 429) {
                toast.error(e.response.data?.detail || "Too many submissions. Please try again in a few minutes.");
            } else {
                toast.error("Sorry, we couldn't submit your intention. Please try again.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handlePray = async (id) => {
        if (prayed.has(id)) return;
        try {
            const data = await prayForIntention(id);
            setItems((prev) => prev.map((it) => (it.id === id ? { ...it, pray_count: data.pray_count } : it)));
            const next = new Set(prayed);
            next.add(id);
            setPrayed(next);
            localStorage.setItem("htk-prayed", JSON.stringify([...next]));
        } catch (e) {
            toast.error("Could not record your prayer. Please try again.");
        }
    };

    return (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-14">
            <span className="eyebrow">Pray with us</span>
            <h1 className="heading-display mt-2">Parish Prayer Wall</h1>
            <p className="mt-4 max-w-2xl text-maroon-700/85">
                Submit a prayer intention and our community will lift you up. Click the candle on any
                intention to add your prayer.
            </p>

            <form
                data-testid={TIDS.prayer.form}
                onSubmit={handleSubmit}
                className="mt-8 rounded-2xl shadow-lg p-5 sm:p-6 border"
                style={{ background: "#252525", borderColor: "#900000", color: "#F3EDE4" }}
            >
                <div className="grid gap-4 sm:grid-cols-3">
                    <input
                        data-testid={TIDS.prayer.name}
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your name"
                        maxLength={80}
                        className="sm:col-span-1 rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400"
                        style={{ background: "#1a1a1a", borderColor: "rgba(212,175,55,0.4)", color: "#F3EDE4" }}
                    />
                    <textarea
                        data-testid={TIDS.prayer.intention}
                        value={intention}
                        onChange={(e) => setIntention(e.target.value)}
                        placeholder="Share your prayer intention..."
                        rows={2}
                        maxLength={600}
                        className="sm:col-span-2 rounded-xl border px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400 resize-none"
                        style={{ background: "#1a1a1a", borderColor: "rgba(212,175,55,0.4)", color: "#F3EDE4" }}
                    />
                </div>
                <div className="mt-4 flex items-center justify-between gap-3">
                    {/* Honeypot: hidden from real users, visible to naive bots */}
                    <input
                        type="text"
                        name="website"
                        tabIndex={-1}
                        autoComplete="off"
                        value={hp}
                        onChange={(e) => setHp(e.target.value)}
                        aria-hidden="true"
                        style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                    />
                    <p className="text-xs text-maroon-700/70 italic">
                        Please keep submissions respectful, names and intentions are visible to all visitors.
                    </p>
                    <button
                        type="submit"
                        data-testid={TIDS.prayer.submit}
                        disabled={submitting}
                        className="btn-primary disabled:opacity-60"
                    >
                        {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Submit
                    </button>
                </div>
            </form>

            <div data-testid={TIDS.prayer.list} className="mt-10 grid gap-4 sm:grid-cols-2">
                {loading && (
                    <p className="text-sm text-maroon-700/70">Loading intentions…</p>
                )}
                {!loading && items.length === 0 && (
                    <p className="text-sm text-maroon-700/70">No intentions yet. Be the first to share one.</p>
                )}
                {items.map((it) => {
                    const hasPrayed = prayed.has(it.id);
                    return (
                        <article
                            key={it.id}
                            data-testid={`prayer-card-${it.id}`}
                            className="relative rounded-2xl border border-maroon-100 bg-cream-50 p-5 hover:shadow-md transition-shadow"
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <div className="font-serif text-lg text-maroon-800">{it.name}</div>
                                    <div className="text-[11px] uppercase tracking-[0.18em] text-gold-500/80 mt-0.5">
                                        {formatDate(it.created_at)}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handlePray(it.id)}
                                    data-testid={TIDS.prayer.prayBtn(it.id)}
                                    disabled={hasPrayed}
                                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                                        hasPrayed
                                            ? "border-gold-300 bg-gold-100 text-maroon-700"
                                            : "border-maroon-200 bg-white text-maroon-700 hover:bg-maroon-600 hover:text-cream-50 hover:border-maroon-600"
                                    }`}
                                    title={hasPrayed ? "You've prayed for this intention" : "I'll pray"}
                                >
                                    <Heart size={14} fill={hasPrayed ? "#9a2026" : "none"} />
                                    {it.pray_count} {it.pray_count === 1 ? "prayer" : "prayers"}
                                </button>
                            </div>
                            <div className="gold-divider my-3 opacity-50" />
                            <p className="text-sm text-maroon-700/90 leading-relaxed whitespace-pre-wrap">
                                {it.intention}
                            </p>
                        </article>
                    );
                })}
            </div>
        </div>
    );
};

export default PrayerWall;
